// Copyright © 2025 CATTLEytics Inc.

import { formatInTimeZone } from 'date-fns-tz';
import { Spinner } from 'react-bootstrap';

import Button from '../../common/components/Button';
import { isNullOrWhitespace } from '../../shared';
import { exportToCSV } from './csvExport';
import { DynamicTable } from './DynamicTable';
import { ReportSection } from './ReportSection';
import { useSourceReport } from './useSourceReport';

export function DynamicQuery({ reportName }: { reportName: string }): JSX.Element {
  const query = useSourceReport(reportName);

  if (query.isLoading) {
    return (
      <ReportSection title={reportName}>
        <Spinner />
      </ReportSection>
    );
  }

  if (query.isError) {
    return <ReportSection title={reportName}>Error loading data</ReportSection>;
  }

  if (!Array.isArray(query.data) || query.data.length === 0) {
    return <ReportSection title={reportName}>No data available</ReportSection>;
  }

  // pre-process data
  const processedData = query.data.map((row) => {
    // Convert all values to strings for consistency
    const formattedColumns: Record<string, string> = {};

    if (!isNullOrWhitespace(row['month'])) {
      formattedColumns['month'] = formatInTimeZone(row['month'], 'UTC', 'yyyy-MM-dd');
    }

    // get all keys with "date" in the key
    const dateKeys = Object.keys(row).filter((key) => key.toLowerCase().includes('date'));
    dateKeys.forEach((key) => {
      if (!isNullOrWhitespace(row[key])) {
        formattedColumns[key] = formatInTimeZone(row[key], 'UTC', 'yyyy-MM-dd');
      }
    });

    return { ...row, ...formattedColumns };
  });

  const handleCSVExport = (): void => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${reportName.replace(/\s+/g, '_')}_${timestamp}`;
    exportToCSV(processedData, filename);
  };

  return (
    <ReportSection
      title={reportName}
      titleChildren={
        <Button className="ms-auto" onClick={handleCSVExport}>
          Export to CSV
        </Button>
      }
    >
      <DynamicTable data={processedData} reportName={reportName} />
    </ReportSection>
  );
}
